/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { basicIntegrations, defaultScmResolveUrl } from '../helpers';
import { ScmIntegration, ScmIntegrationsFactory } from '../types';
import {
  GoogleGcsIntegrationConfig,
  readGoogleGcsIntegrationConfig,
  GOOGLE_GCS_HOST,
} from './config';

/**
 * A Google Cloud Storage based integration.
 *
 * @public
 */
export class GoogleGcsIntegration implements ScmIntegration {
  static factory: ScmIntegrationsFactory<GoogleGcsIntegration> = ({
    config,
  }) => {
    const gcsConfig = config.has('integrations.googleGcs')
      ? readGoogleGcsIntegrationConfig(
          config.getConfig('integrations.googleGcs'),
        )
      : { host: GOOGLE_GCS_HOST };

    return basicIntegrations(
      [new GoogleGcsIntegration(gcsConfig)],
      i => i.config.host,
    );
  };

  get type(): string {
    return 'googleGcs';
  }

  get title(): string {
    return this.integrationConfig.host;
  }

  get config(): GoogleGcsIntegrationConfig {
    return this.integrationConfig;
  }

  constructor(private readonly integrationConfig: GoogleGcsIntegrationConfig) {}

  resolveUrl(options: {
    url: string;
    base: string;
    lineNumber?: number | undefined;
  }): string {
    return defaultScmResolveUrl(options);
  }

  resolveEditUrl(url: string): string {
    return url;
  }
}
